import {
  CALLDATE_OPTIONS,
  COUPONS_OPTIONS,
  INFO_STRUCT_OPTIONS,
  MIN_MAX_DOUBLE_OPTIONS,
  MIN_MAX_FLOAT_OPTIONS,
  PUT_DATE_OPTIONS,
  REFUNDING_DATE_OPTIONS,
  USER_INPUT_OPTIONS,
} from '@/constants/portfolioOptimization.constant'
import { ScreeningItemType } from '@/enums/portfolio-optimization.enum'
import {
  IScreenSecuritiesPayload,
  IScreeningItemDetails,
  IScreeningItemPortfolios,
  IScrreeningCriteriaPayload,
  ISecurity,
  ScreeningOption,
} from '@/interfaces/portfolioOptimization.interface'
import axiosInstance from '@/utils/interceptor'

export const getCandidateScreenSecurities = async (
  reqBody: IScreenSecuritiesPayload,
): Promise<ISecurity[]> => {
  const response = await axiosInstance.post('screening-securities', reqBody)
  return response.data as ISecurity[]
}

export const getCandidateScreeningItems = async (
  filterTo?: string
): Promise<ScreeningOption[]> => {
  let url = 'screening-items'
  
  // Add filterTo query parameter if provided
  if (filterTo) {
    url += `?filterTo=${encodeURIComponent(filterTo)}`
  }
  
  const response = await axiosInstance.get(url)
  return response.data as ScreeningOption[]
}

export const getCandidateScreeningItemDetails = async (
  itemId: string,
  date?: string,
): Promise<IScreeningItemDetails> => {
  let url = `screening-items/${itemId}`
  if (itemId === ScreeningItemType.PORTFOLIO && date) {
    url += `?date=${date}`
  }
  const response = await axiosInstance.get(url)
  return response.data as IScreeningItemDetails
}

export const saveNewScreeningCriteria = async (
  payload: IScrreeningCriteriaPayload,
): Promise<{ success: boolean; result: string }> => {
  const response = await axiosInstance.post('screening-templates', payload)
  return response.data as { success: boolean; result: string }
}
export const updateScreeningCriteriaTemplate = async (
  reqBody: IScrreeningCriteriaPayload,
): Promise<{ success: boolean; result: string }> => {
  const response = await axiosInstance.put(
    `screening-templates/${reqBody.id}`,
    reqBody,
  )
  return response.data as { success: boolean; result: string }
}

export const computeScreenItemData = async (
  payload: any,
): Promise<{ success: boolean; result: string }> => {
  const response = await axiosInstance.post(
    '/screening-templates/computeScreenItemData',
    payload,
  );
  return response.data as { success: boolean; result: string };
};

export const getCandidateScreeningTemplates = async (): Promise<
  { shortName: string; longName: string }[]
> => {
  const response = await axiosInstance.get('screening-templates')
  return response.data as { shortName: string; longName: string }[]
}

export const getCandidateScreeningTemplateDetails = async (
  templateId: string,
): Promise<any> => {
  const response = await axiosInstance.get(`screening-templates/${templateId}`)
  return response.data
}

export const getScreeningTemplatePayload = (
  screenCriteriaData: any,
  screenCriteriaName: string,
) => {
  if (!screenCriteriaData?.configOptions) {
    return {}
  }

  let payload: IScrreeningCriteriaPayload = {
    name: screenCriteriaName,
    criterias: [],
  } as IScrreeningCriteriaPayload
  let selectedDataValue:
    | string[]
    | { min: number; max: number }
    | { [key: string]: string | number | boolean } = []
  const config = screenCriteriaData.configOptions
  if (config.hasPortfolioGrid) {
    selectedDataValue = screenCriteriaData.data.map(
      (item: IScreeningItemPortfolios) => item.shortName,
    )
  } else if (config.hasUserInput) {
    selectedDataValue = screenCriteriaData.data.inputValues
  } else if (
    (screenCriteriaData.selectedScreeningItem.id ===
      ScreeningItemType.MEMCODE &&
      config.hasBenchmark) ||
    (isOptionMatch(
      screenCriteriaData.selectedScreeningItem.id,
      INFO_STRUCT_OPTIONS,
    ) &&
      config.hasInvert)
  ) {
    selectedDataValue = screenCriteriaData.data.selectedTitles
  } else if (
    (screenCriteriaData.selectedScreeningItem.id === ScreeningItemType.COUP &&
      (config.hasMinMaxFloat ||
        config.hasHalfCouponsExcluded ||
        config.hasHalfCouponsIncluded ||
        config.hasAllCoupons)) ||
    (screenCriteriaData.selectedScreeningItem.id ===
      ScreeningItemType.FSTREFD &&
      (config.hasMinMaxDate ||
        config.hasRefundableSecuritiesOnly ||
        config.hasNonRefundableSecuritiesOnly ||
        config.hasNoSelection)) ||
    (screenCriteriaData.selectedScreeningItem.id ===
      ScreeningItemType.PUTDATE &&
      (config.hasMinMaxDate ||
        config.hasNoSelection ||
        config.hasNonPutableSecuritiesOnly ||
        config.hasPutableSecuritiesOnly)) ||
    (screenCriteriaData.selectedScreeningItem.id ===
      ScreeningItemType.CALLDATE &&
      (config.hasMinMaxDate ||
        config.hasNoSelection ||
        config.hasCallable ||
        config.hasCallableMakeWhole ||
        config.hasCallableNonMakeWhole ||
        config.hasNonCallable ||
        config.hasNonCallableLife)) ||
    (screenCriteriaData.selectedScreeningItem.id ===
      ScreeningItemType.CURWALA &&
      config.hasMinMaxShort) ||
    isSelectedOptionTypeMatch(
      screenCriteriaData.selectedScreeningItem.id,
      config,
    )
  ) {
    selectedDataValue = {
      min: Number(screenCriteriaData.data.min),
      max: Number(screenCriteriaData.data.max),
    }
  }
  payload['criterias'].push({
    id: screenCriteriaData.selectedScreeningItem.id,
    data: {
      selectedData: selectedDataValue,
      itemType: screenCriteriaData.itemType,
      configSetting: getConfigSettings(screenCriteriaData, config),
    },
  })
  return payload
}

const getConfigSettings = (
  screenCriteriaData: any,
  configOptions: { [key: string]: boolean },
) => {
  let configSetting = {}
  let isOptionMatched = isSelectedOptionTypeMatch(
    screenCriteriaData.selectedScreeningItem.id,
    configOptions,
  )
  if (isOptionMatched) {
    configSetting = {
      isInclude: screenCriteriaData.data.isInclude,
    }
    return configSetting
  }
  switch (screenCriteriaData.selectedScreeningItem.id) {
    case ScreeningItemType.COUP:
      configSetting = {
        isInclude: screenCriteriaData.data.isInclude,
        isAllCoupons:
          screenCriteriaData.data.selectedSettingItem === COUPONS_OPTIONS[0],
        isHalfCouponsExcluded:
          screenCriteriaData.data.selectedSettingItem === COUPONS_OPTIONS[1],
        isHalfCouponsIncluded:
          screenCriteriaData.data.selectedSettingItem === COUPONS_OPTIONS[2],
      }
      break
    case ScreeningItemType.FSTREFD:
      configSetting = {
        isInclude: screenCriteriaData.data.isInclude,
        isNoSelection:
          screenCriteriaData.data.selectedSettingItem ===
          REFUNDING_DATE_OPTIONS[0],
        isRefundableSecuritiesOnly:
          screenCriteriaData.data.selectedSettingItem ===
          REFUNDING_DATE_OPTIONS[1],
        isNonRefundableSecuritiesOnly:
          screenCriteriaData.data.selectedSettingItem ===
          REFUNDING_DATE_OPTIONS[2],
      }
      break
    case ScreeningItemType.PUTDATE:
      configSetting = {
        isInclude: screenCriteriaData.data.isInclude,
        isNoSelection:
          screenCriteriaData.data.selectedSettingItem === PUT_DATE_OPTIONS[0],
        isPutableSecuritiesOnly:
          screenCriteriaData.data.selectedSettingItem === PUT_DATE_OPTIONS[1],
        isNonPutableSecuritiesOnly:
          screenCriteriaData.data.selectedSettingItem === PUT_DATE_OPTIONS[2],
      }
      break
    case ScreeningItemType.CALLDATE:
      configSetting = {
        isInclude: screenCriteriaData.data.isInclude,
        isNoSelection:
          screenCriteriaData.data.selectedSettingItem === CALLDATE_OPTIONS[0],
        isCallable:
          screenCriteriaData.data.selectedSettingItem === CALLDATE_OPTIONS[1],
        isCallableMakeWhole:
          screenCriteriaData.data.selectedSettingItem === CALLDATE_OPTIONS[2],
        isCallableNonMakeWhole:
          screenCriteriaData.data.selectedSettingItem === CALLDATE_OPTIONS[3],
        isNonCallable:
          screenCriteriaData.data.selectedSettingItem === CALLDATE_OPTIONS[4],
        isNonCallableLife:
          screenCriteriaData.data.selectedSettingItem === CALLDATE_OPTIONS[5],
      }
      break
    case ScreeningItemType.CURWALA:
      configSetting = {
        isInclude: screenCriteriaData.data.isInclude,
      }
      break
  }
  if (!Object.keys(configSetting).length && configOptions.hasIncludeExclude) {
    configSetting = {
      isInclude: screenCriteriaData.data.isInclude,
    }
  }
  return configSetting
}

export const isOptionMatch = (id: string, optionsList: string[]) =>
  optionsList.find((numOption) => numOption === id)

export const isSelectedOptionTypeMatch = (
  id: string,
  configOptions: { [key: string]: boolean },
) => {
  if (
    (isOptionMatch(id, MIN_MAX_DOUBLE_OPTIONS) &&
      configOptions.hasMinMaxDouble) ||
    (isOptionMatch(id, MIN_MAX_FLOAT_OPTIONS) &&
      configOptions.hasMinMaxFloat) ||
    (isOptionMatch(id, USER_INPUT_OPTIONS) &&
      configOptions.hasUserInput &&
      configOptions.hasIncludeExclude)
  ) {
    return true
  }
  return false
}
export const createComputeScreenItemDataStatement = (
  selectedScreeningOption: { id: string },
  criteriaValues: any,
  type: string,
  options?: { [key: string]: string | number | boolean },
) => {
  return {
    id: selectedScreeningOption.id,
    data: {
      itemType: type,
      selectedData: criteriaValues,
      configSetting: options,
    },
  }
}
export const createComputeScreenItemDataPayload = (
  selectedScreeningOption: { id: string },
  minMaxValues: { min: string; max: string },
  type: string,
  options?: { [key: string]: string | number | boolean },
) => {
  return {
    id: selectedScreeningOption.id,
    data: {
      itemType: type,
      selectedData: {
        min: Number(minMaxValues.min),
        max: Number(minMaxValues.max),
      },
      configSetting: options,
    },
  }
}
